import ZoomCard from "./zoomCard";
import { getHeldTokens } from "../../components/getTokens";
import { useEffect, useState } from "react";
import { getCollToksByWallet, getNftsMetadataByCid } from "../../lib/api";
import MetaModal from "./MetaModal";
import { imgErr } from "../../components/imgErrHandler/imgErrHandler";
import { useAppKitAccount, useAppKitProvider } from "@reown/appkit/react";
import { ethers } from "ethers";
import { useAppKitNetwork } from "@reown/appkit/react";
import { chainData } from "../../chains";

/**
 * OwnedTokens component.
 *
 * Displays the current user's owned and listed tokens for a collection.
 * Fetches token ownership data from backend and on-chain verification.
 * Shows two sections:
 * - Tokens owned but not listed (cTokens)
 * - Tokens currently listed for sale (aTokens)
 *
 * Uses ZoomCard components to display each token with modal callback.
 *
 * @param {Object} props - Component props
 * @param {Object} props.cData - Collection data with id, contractAddress, network
 * @param {Function} props.modalCallback - Callback to show item details modal (coll, tokId)
 * @param {Function} props.setMC - Setter for modal content
 * @returns {React.ReactElement} Token ownership summary or null
 */
export const OwnedTokens = ({cData, modalCallback, setMC}) => {


    const {address, isConnected} = useAppKitAccount();
    const {chainId} = useAppKitNetwork();
    
    const [toks, setToks] = useState(null);
    const [ownTokens, sOT] = useState(0)
    const [selTokens, sST] = useState(0);
    
    useEffect(() => {
        // Only proceed if we have everything needed for the fetch.
        if (!address || !cData?.contractAddress || !chainId) {
            setToks(null);
            sOT(0);
            sST(0);
            return;
        }
        
        const processAndSetTokens = (nfts, onChainData) => {
            const metadataMap = new Map();
            nfts.forEach(item => {
                if (item && item.tokId) {
                    try {
                        item.meta = JSON.parse(item.metadata);
                        item.image = item.meta.image;
                    } catch (e) {
                        item.meta = {};
                    }
                    metadataMap.set(item.tokId.toString(), item);
                }
            });

            const cTokens = (onChainData.collection?.tokens || []).map(t => ({ ...t, ...metadataMap.get(t.id.toString()) }));
            const aTokens = (onChainData.auction?.tokens || []).map(t => ({ ...t, ...metadataMap.get(t.id.toString()) }));

            const finalToks = { cTokens, aTokens };
            setToks(finalToks);
            sOT(finalToks.cTokens.length);
            sST(finalToks.aTokens.length);
        };

        const loadAndVerifyTokens = async () => {
            // Step 1: Optimistic load from the backend
            const cachedData = await getCollToksByWallet({ collId: cData.id, owner: address });

            if (cachedData?.nfts?.length) {
                // Temporarily create a structure that processAndSetTokens can use
                const pseudoOnChainData = {
                    collection: { tokens: cachedData.tokenIds.map(id => ({ id })) },
                    auction: { tokens: [] } // Assume no auction data from this endpoint
                };
                processAndSetTokens(cachedData.nfts, pseudoOnChainData);
            }

            // Step 2: Background verification with RPC
            const rpcUrl = chainData[cData.network]?.rpc;
            if (!rpcUrl) {
                console.error(`No RPC URL found for network: ${cData.network}`);
                return;
            }
            const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
            const onChainData = await getHeldTokens(address, cData.network, cData.contractAddress, provider);

            // Step 3: Compare and reconcile
            const onChainIds = [
                ...(onChainData.collection?.ids || []),
                ...(onChainData.auction?.ids || [])
            ].map(String).sort();

            const cachedIds = (cachedData?.tokenIds || []).map(String).sort();

            const areEqual = onChainIds.length === cachedIds.length && onChainIds.every((val, index) => val === cachedIds[index]);

            if (areEqual) {
                // If we already have the metadata, we can enrich the on-chain data and set it again
                // to ensure the auction/collection split is correct.
                if (cachedData?.nfts) {
                    processAndSetTokens(cachedData.nfts, onChainData);
                }
                return;
            }

            if (onChainIds.length > 0) {
                // The cache was wrong, so we must fetch the correct metadata for the on-chain tokens.
                const freshNfts = await getNftsMetadataByCid({
                    collid: cData.id,
                    id: onChainIds,
                    limit: onChainIds.length
                });
                processAndSetTokens(freshNfts, onChainData);
            } else {
                // The user owns no tokens on-chain.
                setToks({ cTokens: [], aTokens: [] });
                sOT(0);
                sST(0);
            }
        };

        loadAndVerifyTokens();
    }, [address, cData, chainId]);

    return ((ownTokens > 0 || selTokens > 0) ? <><span className={"text-green text-tiny"}>

        {/* The iErr prop was missing here, which is good practice to add back. */}
        {ownTokens > 0 ?
            <>You own {ownTokens} item{ownTokens > 1 ? "s" : null} in this
                collection:<br/>
                <div style={{
                    display: "flex",
                    flexWrap: "wrap"
                }}>
                    {toks?.cTokens?.length ? toks.cTokens.filter(Boolean).map((items_) =>
                        <>

                            <ZoomCard cDat={cData} item={items_}
                                      callback={(a,b) => modalCallback(a,b)}/>

                        </>
                    ) : null}</div>
                <br/></> : null}
        {selTokens > 0 ?
            <> You are selling {selTokens} item{selTokens > 1 ? "s" : null} in this
                collection:<br/>
                <div style={{display: "flex", flexWrap: "wrap"}}>
                    {toks?.aTokens?.length ? toks.aTokens.filter(Boolean).map((items_) => <>

                            <ZoomCard cDat={cData} item={items_} iErr ={(e)=>imgErr(e, items_)}
                                      callback={(a,b) => modalCallback(a, b)}/>
                        </>
                    ) : null}</div>
                <br/></> : null}</span>
    </> : null);
}